import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Button, FlowGrid, H6, Paragraph, Row, Separator, QuickGlanceOverlay } from '@fs/zion-ui'
import { FormTextField, useZionFormContext } from '@fs/zion-form'
import { useSlsStateContext, getDisplayLabelForFieldType } from '@fs/zion-sls'

const FamilyMemberNameFormOverlay = ({ overlay, index, basePath = 'newPersonName' }) => {
  const [t] = useTranslation()
  const { types: slsTypes } = useSlsStateContext()
  const { trigger, getValues, resetField, setValue } = useZionFormContext()

  const handleSave = async () => {
    const isValid = await trigger([`${basePath}.givenName`, `${basePath}.surname`])
    if (isValid) {
      const [nameValues, targetRecordId, formId] = getValues([
        basePath,
        'targetRecordId',
        `relationships[${index}].formId`,
      ])
      const givenName = nameValues?.givenName?.trim()
      const surname = nameValues?.surname?.trim()

      if (givenName || surname) {
        // Update Form State
        const nameParts = [givenName, surname].filter(Boolean).join(' ')
        setValue(
          `relationships[${index}].value`,
          { key: 'NEW_PERSON', primaryText: nameParts, recordId: targetRecordId },
          { shouldDirty: true }
        )

        // Explicitly set the newPerson data to ensure it persists when the overlay closes
        setValue(`relationships[${index}].newPerson`, { givenName, surname }, { shouldDirty: true })
        setValue(`relationships[${index}].personId`, `NEW_PERSON-${formId}`, { shouldDirty: true })

        overlay.close()
      }
    }
  }

  const handleCancel = () => {
    resetField(`${basePath}.givenName`)
    resetField(`${basePath}.surname`)
    overlay.close()
  }

  return (
    <QuickGlanceOverlay {...overlay} onClose={handleCancel}>
      <FlowGrid orientation="vertical" columnCount={1} gutters="nano">
        <FlowGrid orientation="vertical" columnCount={1} gutters="xxs">
          <H6>{t('family-members-card.add-person-title', 'Add Person')}</H6>
          <Paragraph size="sm">
            {t(
              'family-members-card.add-person-description',
              "Add the person's name. You'll add more information about them later."
            )}
          </Paragraph>
        </FlowGrid>

        <Row gutters="xxs">
          <FormTextField
            name={`${basePath}.givenName`}
            label={getDisplayLabelForFieldType('NAME_GN', slsTypes)}
            data-testid="new-person-name-NAME_GN-field"
            dense
            growX
          />
        </Row>
        <Row gutters="xxs">
          <FormTextField
            name={`${basePath}.surname`}
            label={getDisplayLabelForFieldType('NAME_SURN', slsTypes)}
            data-testid="new-person-name-NAME_SURN-field"
            dense
            growX
          />
        </Row>

        <Separator size="xs" />
        <FlowGrid gutters="xxs" columnCount={2} orientation="horizontal">
          <Button
            emphasis="medium"
            onClick={handleCancel}
            fullWidth
            data-testid="family-member-name-creation-cancel-button"
            linkName="family-member-name-creation-cancel-button"
          >
            {t('family-members-card.cancel.btn', 'Cancel')}
          </Button>
          <Button
            emphasis="high"
            fullWidth
            onClick={handleSave}
            data-testid="family-member-name-creation-save-button"
            linkName="family-member-name-creation-save-button"
          >
            {t('family-members-card.save.btn', 'Save')}
          </Button>
        </FlowGrid>
      </FlowGrid>
    </QuickGlanceOverlay>
  )
}

FamilyMemberNameFormOverlay.propTypes = {
  index: PropTypes.number.isRequired,
  basePath: PropTypes.string,
  overlay: PropTypes.shape({
    isOpen: PropTypes.bool,
  }).isRequired,
}

export default FamilyMemberNameFormOverlay
